import { useLayoutEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { focusField } from '../components/focus/focusUtils';
import { BuildSchema, useBuildFormContext } from '../form/schema';

import { type BuildStep } from './BuildHeader';
import { useValidateQuizList } from './useValidateQuizList';

export const useBuildStep = () => {
  const [step, setStep] = useBuildStepWithInitialTarget();

  const [isSubmitButtonShown, setIsSubmitButtonShown] = useState(
    () => step === 'settings',
  );

  const { getValues } = useBuildFormContext();

  const validateQuizList = useValidateQuizList();

  const handleStepChange = async (newStep: BuildStep) => {
    if (newStep === 'settings') {
      const isValid = await validateQuizList();

      if (!isValid) {
        return;
      }

      //모두 통과하면 일단 제출 버튼을 보여줌
      setIsSubmitButtonShown(true);
      setStep(newStep);
    } else {
      // 설정 -> 퀴즈
      const formData = getValues();
      const { success, error } = BuildSchema.safeParse(formData);

      // 퀴즈 리스트 이외의 필드에 대해 유효성 검사를 통과하지 못하면 제출 버튼을 숨김
      if (
        !success &&
        error?.errors.some(error => !error.path.includes('quizList'))
      ) {
        setIsSubmitButtonShown(false);
      }
      setStep(newStep);
    }
  };

  return {
    step,
    handleStepChange,
    isSubmitButtonShown,
  };
};

/**
 * 수정 페이지 진입 시, 수정 모드인지 확인하고 router query를 읽어 초기 진입점을 결정합니다.
 * 수정 모드가 아닌 경우, 퀴즈 탭으로 진입합니다.
 */
export const useBuildStepWithInitialTarget = () => {
  const router = useRouter();

  const isUpdate = useMemo(
    () => router.pathname.includes('/update'),
    [router.pathname],
  );

  const initialTab =
    isUpdate && router.query.initialStep === 'settings' ? 'settings' : 'quiz';

  const initialTargetField = router.query.targetField;
  const [step, setStep] = useState<BuildStep>(initialTab as BuildStep);

  useLayoutEffect(() => {
    if (!isUpdate) return;

    if (initialTargetField) {
      focusField(initialTargetField as string, {
        delay: 100,
        scroll: true,
        focus: true,
      });
    }
  }, [initialTargetField, isUpdate]);

  return [step, setStep] as const;
};
